import { api, ticketInput, type TicketInput } from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { Alert, Button, Card, FormGroup, Input, Select, Textarea } from '../../ui';

export const SUPPORT_TABS = [
  { label: 'New Ticket', to: '/support/create' },
  { label: 'My Tickets', to: '/support/list' },
];

const CATEGORIES = ['Account', 'Deposit', 'Withdrawal', 'Income', 'KYC', 'Technical', 'Other'];

export function CreateTicket() {
  const navigate = useNavigate();

  const form = useForm<TicketInput>({
    resolver: zodResolver(ticketInput),
    defaultValues: { subject: '', category: '', priority: 'normal', message: '' },
  });

  const create = useMutation({
    mutationFn: (values: TicketInput) => api.support.create(values),
    onSuccess: (result) => navigate(`/support/view/${result.data.id}`),
  });

  return (
    <>
      <PageHeader title="New Ticket" tabs={SUPPORT_TABS} />

      <Card>
        {create.error ? <Alert tone="danger">{(create.error as Error).message}</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => create.mutate(values))} noValidate>
          <FormGroup label="Subject" error={form.formState.errors.subject?.message}>
            <Input {...form.register('subject')} />
          </FormGroup>

          <FormGroup label="Category" error={form.formState.errors.category?.message}>
            <Select {...form.register('category')}>
              <option value="">Choose a category…</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup label="Priority" error={form.formState.errors.priority?.message}>
            <Select {...form.register('priority')}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </Select>
          </FormGroup>

          <FormGroup label="Message" error={form.formState.errors.message?.message}>
            <Textarea rows={6} {...form.register('message')} />
          </FormGroup>

          <Button type="submit" variant="gold" disabled={create.isPending}>
            {create.isPending ? 'Submitting…' : 'Submit Ticket'}
          </Button>
        </form>
      </Card>
    </>
  );
}
